import { Pub, State, Sub } from '../../../../../shared/state/state.abstract';
import { ITabState, tabStateModel } from '../../../states/tabState.model';
import {
  customerService,
  Bakery2bAccountsRes,
  ConnectBakery2bAccountWithCustomerRes,
  DisconnectBakery2bAccountFromCustomerRes,
} from '../../../services/customer.service';
import { Observable, filter, map, merge, switchMap, tap } from 'rxjs';
import { responseHandler } from '../../../../../shared/responseHandler/responseHandler';
import { customersListState } from '../../../states/customersList.state';
import { customerTabLoadingService } from '../customerTabLoading.service';
import { defaultCustomerBakery2bData } from '../../../loaders/bakery2bTab.resolver';
import {
  C_Global_Error_Code,
  ConnectWa_Bakery2bAccountWithCustomerMutationVariables,
  DisconnectWa_Bakery2bAccountFromCustomerMutationVariables,
} from '../../../../../graphql/generatedModel';

class PubImpl extends Pub<IBakery2bTabState> {
  initBakery2bAccountsData(bakery2bAccounts: ITabState['bakery2bAccounts'], customerId: string) {
    this.emit('initBakery2bAccountsData', {
      bakery2bAccounts,
      customerId,
    });
  }

  addRecord(newRecord: ConnectWa_Bakery2bAccountWithCustomerMutationVariables) {
    this.emit('addRecord', { newRecord });
  }

  deleteRecord(recordToRemove: DisconnectWa_Bakery2bAccountFromCustomerMutationVariables) {
    this.emit('deleteRecord', { recordToRemove });
  }

  clearStream() {
    this.emit(undefined, {});
  }
}

class SubImpl extends Sub<IBakery2bTabState> {
  protected actionHandlers(): Observable<IBakery2bTabState> {
    return merge(this.selectedCustomer(), this.addRecord(), this.deleteRecord()).pipe(
      tap((state) => {
        this.stream$.next({ ...state, action: 'internalUpdate' });
      }),
    );
  }

  private addRecord(): Observable<IBakery2bTabState> {
    return this.actionListener(['addRecord']).pipe(
      tap(({ newRecord }) => {
        if (newRecord) {
          customerService.pub.connectBakery2bAccountWithCustomer(newRecord);
          customerTabLoadingService.pub.loading(true);
        }
      }),
      switchMap((state) => {
        return customerService.sub.connectBakery2bAccountWithCustomer().pipe(
          responseHandler<ConnectBakery2bAccountWithCustomerRes | undefined>({
            success: () => 'customer.customer_saved',
            customErrorHandler: (err) => {
              customerTabLoadingService.pub.loading(false);

              if (
                err.code ===
                C_Global_Error_Code.GEC60_BAKERY2B_USER_ALREADY_CONNECTED_TO_OTHER_COMPANY
              ) {
                return `errors.${err.code}`;
              } else {
                return 'common.error_chnages_not_saved';
              }
            },
          }),
          map((v) => {
            const newState = structuredClone(state);
            customerTabLoadingService.pub.loading(false);
            v && newState.bakery2bAccounts.push(v);
            newState.newRecord = null;
            return newState;
          }),
        );
      }),
    );
  }

  private deleteRecord(): Observable<IBakery2bTabState> {
    return this.actionListener(['deleteRecord']).pipe(
      tap(({ recordToRemove }) => {
        customerService.pub.disconnectBakery2bAccountFromCustomer({
          customerId: recordToRemove?.customerId ?? '',
          bakery2BClientId: recordToRemove?.bakery2BClientId ?? '',
        });
        customerTabLoadingService.pub.loading(true);
      }),
      switchMap((state) => {
        return customerService.sub.disconnectBakery2bAccountFromCustomer().pipe(
          responseHandler<DisconnectBakery2bAccountFromCustomerRes | undefined>({
            success: () => 'customer.customer_saved',
            customErrorHandler: () => {
              customerTabLoadingService.pub.loading(false);
              return 'common.error_chnages_not_saved';
            },
          }),
          map((v) => {
            let newState = structuredClone(state);
            customerTabLoadingService.pub.loading(false);

            if (v) {
              newState.bakery2bAccounts = newState.bakery2bAccounts.filter(
                (item) => item.id != state.recordToRemove?.bakery2BClientId,
              );
              newState.recordToRemove = null;
            }

            return newState;
          }),
        );
      }),
    );
  }

  private selectedCustomer(): Observable<IBakery2bTabState> {
    return customersListState.sub.state().pipe(
      filter(({ action }) => action === 'selectRecord'),
      filter(({ selectedRecord }) => typeof selectedRecord?.id === 'string'),
      switchMap(({ selectedRecord }) => {
        customerTabLoadingService.pub.loading(true);
        const id = selectedRecord!.id;
        const state = this.stream$.getValue();
        state.action = 'list.selectedRecord';
        return this.details(state, id);
      }),
    );
  }

  private details(state: IBakery2bTabState, id: string): Observable<IBakery2bTabState> {
    const details = customerService.sub.getCustomerBakery2bAccounts().pipe(
      responseHandler<Bakery2bAccountsRes>({
        errorReturnType: defaultCustomerBakery2bData,
      }),
      map((data) => {
        state.bakery2bAccounts = data;
        state.customerId = id;
        customerTabLoadingService.pub.loading(false);
        return state;
      }),
    );
    customerService.pub.getCustomerBakery2bAccounts({ id });
    return details;
  }
}

class Bakery2bTabState extends State<IBakery2bTabState> {
  pub = new PubImpl(this.stream$);
  sub = new SubImpl(this.stream$);
}

export const bakery2bTabState = new Bakery2bTabState({
  ...tabStateModel,
  action: undefined,
  bakery2bAccounts: [],
  recordToRemove: null,
  newRecord: null,
});

export interface IBakery2bTabState extends Pick<ITabState, 'customerId'> {
  action:
    | undefined
    | 'initBakery2bAccountsData'
    | 'list.selectedRecord'
    | 'addRecord'
    | 'deleteRecord'
    | 'internalUpdate';
  bakery2bAccounts: ITabState['bakery2bAccounts'];
  recordToRemove: DisconnectWa_Bakery2bAccountFromCustomerMutationVariables | null;
  newRecord: ConnectWa_Bakery2bAccountWithCustomerMutationVariables | null;
}
